// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { restApiUrl } from 'appConstants';
import { getCSRFCookie } from 'axios_middleware';

// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
// https://redux-toolkit.js.org/rtk-query/usage/prefetching

export type ProjectUpdateInput = {
  are_alerts_enabled?: boolean;
  project_name?: string;
  center?: any;
};

export type ProjectSettingUpdateInput = {
  are_alerts_enabled?: boolean;
  is_default?: boolean;
};

export type SavedRouteResponse = {
  id: string;
  name: string;
};

export type ProjectResponse = {
  project_name: string;
  center: any;
  are_alerts_enabled: boolean;
  /** Users with a subscription membership for this project's subscription (sorted by email). */
  subscribed_emails: string[];
  saved_routes: SavedRouteResponse[];
};

export type ProjectSettingResponse = {
  id: number;
  project_id: number;
  are_alerts_enabled: boolean;
};

export const projectApiSlice = createApi({
  reducerPath: 'project_api',
  baseQuery: fetchBaseQuery({
    baseUrl: restApiUrl,
    prepareHeaders: (headers) => {
      headers.append('X-CSRFToken', getCSRFCookie());
    },
  }),
  tagTypes: ['projects', 'projectSettings'],
  endpoints: (builder) => ({
    getProject: builder.query<ProjectResponse, string>({
      query: (project_slug) => `projects/${project_slug}/`,
      providesTags: (result, error, project_slug) => [
        { type: 'projects', id: project_slug },
      ],
    }),

    getProjectSetting: builder.query<ProjectSettingResponse, number>({
      query: (id) => `project-settings/${id}/`,
      providesTags: (result, error, id) => [{ type: 'projectSettings', id }],
    }),

    updateProject: builder.mutation<
      ProjectResponse,
      { project_slug: string; data: ProjectUpdateInput }
    >({
      query: ({ project_slug, data }) => ({
        url: `projects/${project_slug}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { project_slug }) => [
        { type: 'projects', id: project_slug },
      ],
    }),

    updateProjectSetting: builder.mutation<
      ProjectSettingResponse,
      { id: number; data: ProjectSettingUpdateInput }
    >({
      query: ({ id, data }) => ({
        url: `project-settings/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'projectSettings', id },
      ],
    }),
  }),
});

export const {
  useGetProjectQuery,
  useGetProjectSettingQuery,
  useUpdateProjectMutation,
  useUpdateProjectSettingMutation,
} = projectApiSlice;
